/* ═══════════════════════════════════════════════════════════════════════
   Signing in, and choosing a password.

   Two small screens that share one shell, because they are the same moment
   from different directions: someone standing outside their own business.

   The tone is deliberately unlike the customer intake. A customer is being
   looked after; a business owner is being let in. Fewer words, no warmth
   for its own sake, and the errors say exactly what went wrong — with the
   one exception the server insists on, where a wrong password and a
   missing account have to look identical.
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { useHomeHref } from "./session";
import { DashboardLink } from "./components/chrome";
import { CraigFace } from "./components/CraigFace";
import { LetCraigDoIt, Wordmark } from "./components/Brand";
import { PasswordField } from "./components/PasswordField";

type Mode = "sign-in" | "forgot" | "password";

export function Account({ mode }: { mode: Mode }) {
  const [theme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  /* Usually the front door, since anyone on this screen is by definition
     outside. Not always: a signed-in owner who followed an old sign-in
     link from an email should be put back on their dashboard rather than
     handed a form for the account they are already using. */
  const home = useHomeHref();
  useEffect(() => {
    applyTheme(theme);
    // crAIg's own colour: this is his door, not any tenant's.
    applyAccent(undefined, theme);
  }, [theme]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* No top bar for a stranger — this screen is a door and a door with
          navigation on it is a lobby. It appears only for somebody already
          signed in, who got here by following an old link and wants out. */}
      {home === "/dashboard" && (
        <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
          <div className="max-w-4xl mx-auto px-5 py-3 flex items-center">
            <DashboardLink />
          </div>
        </header>
      )}
      <main className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full" style={{ maxWidth: 400 }}>
          {/* The mark is the way out. These pages are crAIg's own, so the
              usual objection doesn't apply — and someone who followed a
              stale link here should not be trapped on a form they have no
              account for. */}
          <a
            href={home}
            className="flex flex-col items-center gap-2 mb-6"
            style={{ textDecoration: "none", color: "inherit" }}
            aria-label={home === "/dashboard" ? "Back to your dashboard" : "Back to the home page"}
          >
            <CraigFace size={44} />
            <Wordmark size={18} />
          </a>
          {mode === "password" ? <SetPassword /> : <SignIn startAt={mode} />}

          {/*
            The way in, for anyone who arrived at the door without a key.

            Not shown while they're choosing a password: they demonstrably
            have an account, and offering to make them a second one at that
            exact moment is how you end up with duplicate businesses.

            This used to point at the landing page, and said so at length —
            because at the time the setup conversation WAS the signup and
            there was no form to send anybody to. There is one now, and a
            person who came here to sign in and found they had no account
            wants the form rather than the pitch.
          */}
          {mode !== "password" && (
            <p style={{ textAlign: "center", fontSize: 13.5, color: "var(--ink-3)", marginTop: 18 }}>
              No account yet?{" "}
              <a href="/signup" style={{ color: "var(--accent)", fontWeight: 600 }}>
                Create one
              </a>
            </p>
          )}
        </div>
      </main>
      <LetCraigDoIt />
    </div>
  );
}

/* ── Signing in ─────────────────────────────────────────────────────────── */

function SignIn({ startAt }: { startAt: Mode }) {
  const [forgot, setForgot] = useState(startAt === "forgot");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (forgot) {
        await fetch("/api/account/forgot", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        // Always the same answer, whether or not the address is one of
        // ours — the server refuses to say, and so does this.
        setSent(true);
        return;
      }
      const res = await fetch("/api/account/sign-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? "That didn't work.");
        return;
      }
      window.location.href = "/dashboard";
    } catch {
      setError("Couldn't reach us just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Panel title="Check your email">
        <p style={{ fontSize: 14.5, color: "var(--ink-2)" }}>
          If <strong>{email}</strong> has an account, a link to set a new password is on its
          way. It works once and expires in a day.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={forgot ? "Reset your password" : "Sign in"}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && email.trim()) void submit();
        }}
      >
        <Field label="Email">
          <input
            className="field"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {!forgot && (
          <PasswordField
            id="signin-password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
        )}

        {error && <Problem>{error}</Problem>}

        <button
          type="submit"
          className="btn btn-primary btn-term w-full"
          disabled={busy || !email.trim() || (!forgot && !password)}
        >
          {busy ? "…" : forgot ? "Send me a link" : "Sign in"}
        </button>
      </form>

      <button
        type="button"
        onClick={() => {
          setForgot((f) => !f);
          setError(null);
        }}
        style={{
          marginTop: 14,
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          fontSize: 13,
          color: "var(--ink-3)",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {forgot ? "Back to signing in" : "I've forgotten my password"}
      </button>
    </Panel>
  );
}

/* ── Choosing one ───────────────────────────────────────────────────────── */

function SetPassword() {
  const token = new URLSearchParams(window.location.search).get("k") ?? "";
  const [who, setWho] = useState<{ email: string; name: string } | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Checked before the form is shown, so nobody composes a password into a
     page that is about to reject it. */
  useEffect(() => {
    fetch(`/api/account/password?k=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(body.error);
        setWho(body);
      })
      .catch((e: Error) => setDead(e.message));
  }, [token]);

  if (dead) {
    return (
      <Panel title="That link's no good">
        <p style={{ fontSize: 14.5, color: "var(--ink-2)" }}>{dead}</p>
        <a href="/account/sign-in" className="btn btn-quiet btn-term w-full" style={{ marginTop: 14 }}>
          Ask for a new one
        </a>
      </Panel>
    );
  }

  if (!who) {
    return (
      <Panel title="One moment">
        <p style={{ fontSize: 14, color: "var(--ink-3)" }}>Checking that link…</p>
      </Panel>
    );
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/account/password?k=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? "That didn't work.");
        return;
      }
      // Setting one signs them in, so there is no reason to make them do it
      // again ten seconds later.
      window.location.href = "/dashboard";
    } catch {
      setError("Couldn't reach us just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Choose a password">
      <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginBottom: 14 }}>
        For <strong>{who.email}</strong>
      </p>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && password) void submit();
        }}
      >
        <PasswordField
          id="new-password"
          label="New password"
          autoFocus
          value={password}
          onChange={setPassword}
        />
        <p style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
          Ten or more, with a capital, a lower-case letter and a number. Three unrelated words and
          a digit is easier to remember and much harder to guess than something clever.
        </p>

        {error && <Problem>{error}</Problem>}

        <button
          type="submit"
          className="btn btn-primary btn-term w-full"
          disabled={busy || password.length < 10}
        >
          {busy ? "…" : "Set it and sign in"}
        </button>
      </form>
    </Panel>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel panel-ticks px-5 py-5">
      <h1 className="mono" style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>
        <span style={{ color: "var(--accent)" }}>&gt;</span> {title}
      </h1>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="term-label" style={{ display: "block", marginBottom: 5 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      style={{
        fontSize: 13.5,
        color: "var(--danger)",
        background: "var(--warn-tint)",
        border: "1px solid var(--warn)",
        borderRadius: "var(--radius)",
        padding: "0.5rem 0.7rem",
      }}
    >
      {children}
    </p>
  );
}
